import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  useDisclosure,
  SimpleGrid,
  HStack,
  Divider,
  Spinner,
  Text,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
} from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import ShadowBox from "components/common/ShadowBox/ShadowBox";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

type CostCenter = {
  _id?: string;
  costCenter: string;
  processName: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
};

const initialForm: CostCenter = {
  costCenter: "",
  processName: "pr",
};

export default function CostCenterScreen(): JSX.Element {
  const { toastNotify } = useContext(AppContext);

  const [form, setForm] = useState<CostCenter>(initialForm);
  const [list, setList] = useState<CostCenter[]>([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [searchCostCenter, setSearchCostCenter] = useState("");
  const [searchProcess, setSearchProcess] = useState("");

  const [debouncedCostCenter, setDebouncedCostCenter] = useState("");
  const [debouncedProcess, setDebouncedProcess] = useState("");

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [isViewOnly, setIsViewOnly] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const [deleteTargetId, setDeleteTargetId] = useState<string | undefined>(undefined);

  const handleChange = (name: keyof CostCenter, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const fetchCostCenters = async () => {
    try {
      setLoading(true);
      const res: any = await appRequest("costCenter", "getAll", {
        page,
        limit,
        costCenter: debouncedCostCenter || undefined,
        processName: debouncedProcess || undefined,
      });

      if (res?.status === "success") {
        setList(res?.data?.data || []);
        setTotalPages(res?.data?.totalPages || 1);
        setTotalCount(res?.data?.totalCount || 0);
      }
    } catch (err: any) {
      toastNotify({
        title: "Fetch failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCostCenter(searchCostCenter);
      setDebouncedProcess(searchProcess);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchCostCenter, searchProcess]);

  useEffect(() => {
    fetchCostCenters();
  }, [page, limit, debouncedCostCenter, debouncedProcess]);

  useEffect(() => {
    setPage(1);
  }, [debouncedCostCenter, debouncedProcess]);

  const handleAdd = async () => {
    if (!form.costCenter) {
      toastNotify({
        title: "Validation error",
        message: "Cost Center is required",
        type: "error",
      });
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        await appRequest("costCenter", "update", { id: editingId, ...form });
      } else {
        await appRequest("costCenter", "create", form);
      }

      toastNotify({
        title: "Success",
        message: editingId ? "Updated successfully" : "Saved successfully",
        type: "success",
      });

      setForm(initialForm);
      setEditingId(undefined);
      fetchCostCenters();
      onClose();
    } catch (err: any) {
      toastNotify({
        title: "Operation failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      setLoading(true);
      await appRequest("costCenter", "delete", { id: deleteTargetId });

      toastNotify({
        title: "Deleted",
        message: "Cost Center removed successfully",
        type: "success",
      });

      fetchCostCenters();
      onDeleteClose();
    } catch (err: any) {
      toastNotify({
        title: "Delete failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ShadowBox>
      <Flex mb={4} align="center" justify="space-between">
        <Box>
          <Text fontWeight="bold" fontSize="2xl" bgGradient="linear(to-r, blue.500, cyan.400)" bgClip="text">
            Cost Center Master
          </Text>
          <Text fontSize="sm" color="gray.500">Manage internal cost centers and their associated processes</Text>
        </Box>

        <Button
          leftIcon={<span>+</span>}
          colorScheme="blue"
          variant="solid"
          borderRadius="full"
          px={8}
          _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
          onClick={() => {
            setForm(initialForm);
            setEditingId(undefined);
            setIsViewOnly(false);
            onOpen();
          }}
        >
          Add Cost Center
        </Button>
      </Flex>

      <HStack spacing={4} mb={6} bg="gray.50" p={4} borderRadius="xl">
        <CustomInput
          name="searchCostCenter"
          label="Cost Center"
          value={searchCostCenter}
          onChange={(e: any) => setSearchCostCenter(e.target.value)}
        />

        <CustomInput
          name="searchProcess"
          label="Process Name"
          value={searchProcess}
          onChange={(e: any) => setSearchProcess(e.target.value)}
        />
      </HStack>

      <Box mt={3} maxH="60vh" overflowY="auto" borderRadius="xl" border="1px" borderColor="gray.100">
        {loading && list.length === 0 ? (
          <Flex justify="center" p={10}>
            <Spinner color="blue.500" size="xl" thickness="4px" />
          </Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th py={4}>Cost Center</Th>
                <Th py={4}>Process</Th>
                <Th py={4}>Created By</Th>
                <Th py={4}>Last Updated</Th>
                <Th py={4} textAlign="center">Action</Th>
              </Tr>
            </Thead>

            <Tbody>
              {list.map((item) => (
                <Tr key={item._id} _hover={{ bg: "blue.50" }} transition="0.2s">
                  <Td fontWeight="600">{item.costCenter}</Td>
                  <Td>
                    <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2}>
                      {item.processName}
                    </Badge>
                  </Td>
                  <Td fontSize="sm">{item.createdBy}</Td>
                  <Td fontSize="xs" color="gray.600">
                    {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : (item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A")}
                  </Td>
                  <Td>
                    <HStack justify="center" spacing={2}>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        borderRadius="full"
                        onClick={() => {
                          setForm({ ...item });
                          setEditingId(item._id);
                          setIsViewOnly(true);
                          onOpen();
                        }}
                      >
                        View
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        borderRadius="full"
                        onClick={() => {
                          setForm({ ...item });
                          setEditingId(item._id);
                          setIsViewOnly(false);
                          onOpen();
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        onClick={() => {
                          setDeleteTargetId(item._id);
                          onDeleteOpen();
                        }}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {list.length === 0 && !loading && (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={10}>
                    <Text color="gray.500">No cost centers found</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* PAGINATION CONTROLS */}
      <Flex mt={6} align="center" justify="space-between" bg="gray.50" p={3} borderRadius="xl" border="1px" borderColor="gray.100">
        <HStack spacing={2}>
          <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">
            Showing {list.length} of {totalCount} Records
          </Text>
        </HStack>
        
        <HStack spacing={2}>
          <Button
            size="sm"
            variant="outline"
            bg="white"
            isDisabled={page === 1}
            onClick={() => setPage(page - 1)}
            borderRadius="lg"
            _hover={{ bg: "blue.50", color: "blue.600" }}
          >
            Previous
          </Button>
          
          <HStack spacing={1}>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1))
              .map((p, i, arr) => (
                <Box key={p} display="flex" alignItems="center">
                  {i > 0 && arr[i-1] !== p - 1 && <Text px={2} color="gray.400">...</Text>}
                  <Button
                    size="sm"
                    variant={page === p ? "solid" : "ghost"}
                    colorScheme={page === p ? "blue" : "gray"}
                    onClick={() => setPage(p)}
                    borderRadius="lg"
                    minW="36px"
                  >
                    {p}
                  </Button>
                </Box>
              ))}
          </HStack>

          <Button
            size="sm"
            variant="outline"
            bg="white"
            isDisabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            borderRadius="lg"
            _hover={{ bg: "blue.50", color: "blue.600" }}
          >
            Next
          </Button>
        </HStack>

        <HStack spacing={3}>
           <Text fontSize="xs" fontWeight="bold" color="gray.500">ROWS</Text>
           <select 
             style={{ 
               fontSize: '12px', 
               padding: '4px 8px', 
               borderRadius: '8px', 
               border: '1px solid #E2E8F0',
               backgroundColor: 'white',
               fontWeight: 'bold',
               cursor: 'pointer'
             }}
             value={limit}
             onChange={(e) => {
               setLimit(Number(e.target.value));
               setPage(1);
             }}
           >
             <option value={10}>10</option>
             <option value={20}>20</option>
             <option value={50}>50</option>
             <option value={100}>100</option>
           </select>
        </HStack>
      </Flex>

      {/* DRAWER FOR ADD/EDIT */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="right">
        <DrawerOverlay backdropFilter="blur(15px)" bg="blackAlpha.700" />
        <DrawerContent maxW="70%" borderRadius="30px 0 0 30px" boxShadow="-20px 0 50px rgba(0,0,0,0.2)">
          <DrawerCloseButton
            size="lg"
            top="4"
            right="6"
            borderRadius="full"
            bg="whiteAlpha.800"
            _hover={{ bg: "red.50", color: "red.500" }}
            transition="0.3s"
          />
          <DrawerHeader
            borderBottomWidth="1px"
            bgGradient="linear(to-r, blue.600, cyan.400)"
            color="white"
            borderTopLeftRadius="30px"
            py={5}
            px={8}
          >
            <Box>
              <Text fontSize="2xl" fontWeight="900" letterSpacing="tight">
                {isViewOnly ? "MASTER DATA VIEW" : (editingId ? "REFINE COST CENTER" : "CREATE NEW COST CENTER")}
              </Text>
              <Text fontSize="xs" fontWeight="bold" opacity="0.9" textTransform="uppercase">
                {isViewOnly ? "READ-ONLY AUDIT ACCESS" : "Organizational unit configuration"}
              </Text>
            </Box>
          </DrawerHeader>

          <DrawerBody py={6} px={6} bg="gray.100">
            {isViewOnly ? (
              <VStack spacing={6} align="stretch">
                <Box bg="white" p={6} borderRadius="2xl" boxShadow="xl" borderLeft="10px solid" borderLeftColor="blue.500">
                  <HStack mb={6} spacing={4}>
                    <Box w="10px" h="24px" bg="blue.500" borderRadius="full" />
                    <Text fontSize="md" fontWeight="900" color="gray.800" letterSpacing="tight">
                      OPERATIONAL IDENTITY
                    </Text>
                  </HStack>

                  <SimpleGrid columns={2} spacing={8}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">COST CENTER IDENTIFIER</Text>
                      <Text fontSize="xl" fontWeight="900" color="blue.700" letterSpacing="tight">{form.costCenter}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">ASSIGNED PROCESS</Text>
                      <Badge colorScheme="blue" variant="solid" borderRadius="full" px={4} py={1} fontSize="xs">
                        {form.processName.toUpperCase()}
                      </Badge>
                    </VStack>
                  </SimpleGrid>
                </Box>

                <Box p={6} bg="white" borderRadius="2xl" border="1px" borderColor="gray.200" boxShadow="sm">
                  <SimpleGrid columns={3} spacing={6}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">AUTHORIZED BY</Text>
                      <Text fontSize="sm" fontWeight="800" color="gray.700">{form.createdBy || "SYSTEM"}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">INITIAL REGISTRY</Text>
                      <Text fontSize="sm" fontWeight="800" color="gray.700">{form.createdAt ? new Date(form.createdAt).toLocaleString() : "N/A"}</Text>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="2xs" fontWeight="900" color="gray.400" letterSpacing="widest">LAST AUDIT</Text>
                      <Text fontSize="sm" fontWeight="800" color="gray.700">{form.updatedAt ? new Date(form.updatedAt).toLocaleString() : "N/A"}</Text>
                    </VStack>
                  </SimpleGrid>
                </Box>
              </VStack>
            ) : (
              <Box bg="white" p={5} borderRadius="2xl" boxShadow="sm" border="1px" borderColor="gray.100">
                <HStack mb={4} spacing={3}>
                  <Box w="8px" h="20px" bg="blue.500" borderRadius="full" />
                  <Text fontSize="sm" fontWeight="800" color="gray.700" textTransform="uppercase" letterSpacing="widest">
                    Configuration Details
                  </Text>
                </HStack>

                <SimpleGrid columns={1} spacing={6}>
                  <CustomInput
                    name="costCenter"
                    label="Cost Center Identifier"
                    placeholder="e.g. IT-001-CORP"
                    value={form.costCenter}
                    onChange={(e: any) => handleChange("costCenter", e.target.value)}
                  />

                  <Box>
                    <Text fontSize="12px" fontWeight="700" color="gray.600" mb={2} ml={1}>
                      Process Assignment
                    </Text>
                    <select
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "16px",
                        border: "2px solid #EDF2F7",
                        backgroundColor: "#F7FAFC",
                        fontSize: "15px",
                        fontWeight: "500",
                        color: "#2D3748",
                        outline: "none",
                        transition: "0.2s",
                        cursor: "pointer"
                      }}
                      value={form.processName}
                      onChange={(e) => handleChange("processName", e.target.value)}
                    >
                      <option value="pr">Purchase Requisition (PR)</option>
                      <option value="domestic">Domestic Payment</option>
                      <option value="marcan">Marcan Logistics</option>
                      <option value="tillomed">Tillomed Pharma</option>
                      <option value="emerging">Emerging Markets</option>
                      <option value="zuventus">Zuventus Healthcare</option>
                      <option value="foreign">Foreign Remittance</option>
                    </select>
                  </Box>
                </SimpleGrid>
              </Box>
            )}
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px" gap={3} py={4} px={8} bg="white">
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="full" px={8} size="sm">
              {isViewOnly ? "EXIT VIEW" : "CANCEL"}
            </Button>
            {!isViewOnly && (
              <Button 
                colorScheme="blue" 
                onClick={handleAdd} 
                isLoading={loading}
                borderRadius="full"
                px={12}
                h="45px"
                fontSize="sm"
                fontWeight="900"
                boxShadow="0 10px 20px -5px rgba(49, 130, 206, 0.4)"
                _hover={{ transform: "translateY(-2px)", boxShadow: "0 15px 25px -5px rgba(49, 130, 206, 0.5)" }}
                _active={{ transform: "scale(0.98)" }}
              >
                {editingId ? "UPDATE CONFIG" : "COMMIT REGISTRY"}
              </Button>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* DELETE CONFIRM MODAL */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="sm" isCentered>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" />
        <ModalContent borderRadius="2xl" boxShadow="2xl">
          <ModalHeader fontSize="lg" fontWeight="600">
            Confirm Delete
          </ModalHeader>
          <Divider />
          <ModalBody py={5}>
            Are you sure you want to delete this cost center? This action cannot be undone.
          </ModalBody>
          <Divider />
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDelete} isLoading={loading}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ShadowBox>
  );
}
